import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import UserAuthModal from "./UserAuthModal";

const emptyNew = {
  first_name: "",
  last_name: "",
  email: "",
  user_id: "",
  user_ty: "M",
  client: "",
  password: "",
  pkc_mem: "",
  phon_no: "",
};

const TYPE_LABEL = {
  X: "Admin",
  M: "Member",
  A: "Approver",
  T: "Treasurer",
};

const initialsOf = (first, last) =>
  ((first || "").slice(0, 1) + (last || "").slice(0, 1)).toUpperCase();

const PAGE_SIZE = 25;

/* -- Row 1: client / user_id / live initials -- */
function UserIdentitySection({ form, set, isSelf, isNew, liveInit, clients, lockClient }) {
  // If the user already has a client_id not in the loaded list (e.g. orphaned
  // or still loading), make sure the dropdown can still display it.
  const options = (() => {
    const list = clients || [];
    if (form.client && !list.some((c) => c.client_id === form.client)) {
      return [{ client_id: form.client, client_name: form.client }, ...list];
    }
    return list;
  })();

  // The Client field is locked when:
  //  • editing any existing user — admins can't reassign a user to a different
  //    tenant (per user request 2026-06-25, task 2).
  //  • the signed-in admin is themselves tagged to a client — new users can
  //    only inherit that same client (task 3).
  const lockTitle = !isNew
    ? "Client cannot be changed after the user is created"
    : "New users inherit your client and cannot be reassigned";

  return (
    <>
      <div className="form-field">
        <label>Client</label>
        <select
          data-testid="um-client"
          value={form.client || ""}
          onChange={(e) => set("client", e.target.value)}
          disabled={lockClient}
          title={lockClient ? lockTitle : ""}
        >
          <option value="">— none —</option>
          {options.map((c) => (
            <option key={c.client_id} value={c.client_id}>
              {c.client_id}{c.client_name ? ` · ${c.client_name}` : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label>User ID <span className="req">*</span> (8 chars)</label>
        <input
          data-testid="um-uid"
          value={form.user_id}
          maxLength={8}
          disabled={isSelf}
          title={isSelf ? "You can't rename your own ID while signed in" : ""}
          onChange={(e) => set("user_id", e.target.value)}
          required={isNew}
        />
      </div>
      <div className="form-field">
        <label>Initials</label>
        <input
          data-testid="um-init"
          value={liveInit}
          disabled
          title="Auto-derived from first+last name"
        />
      </div>
    </>
  );
}

/* -- Row 2: first / last name -- */
function UserNameSection({ form, set, isNew }) {
  return (
    <>
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>First name <span className="req">*</span></label>
        <input
          data-testid="um-first"
          value={form.first_name}
          maxLength={50}
          onChange={(e) => set("first_name", e.target.value)}
          required={isNew}
        />
      </div>
      <div className="form-field">
        <label>Last name <span className="req">*</span></label>
        <input
          data-testid="um-last"
          value={form.last_name}
          maxLength={50}
          onChange={(e) => set("last_name", e.target.value)}
          required={isNew}
        />
      </div>
    </>
  );
}

/* -- Role, email + (new-user only) password -- */
function RoleEmailSection({ form, set, isSelf, isNew }) {
  return (
    <>
      <div className="form-field">
        <label>Type <span className="req">*</span></label>
        <select
          data-testid="um-ty"
          value={form.user_ty}
          disabled={isSelf}
          onChange={(e) => set("user_ty", e.target.value)}
          title={isSelf ? "You can't change your own role" : ""}
        >
          <option value="M">Member (M)</option>
          <option value="A">Approver (A)</option>
          <option value="T">Treasurer (T)</option>
          <option value="X">Admin (X)</option>
        </select>
      </div>
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Email <span className="req">*</span></label>
        <input
          data-testid="um-email"
          type="email"
          value={form.email}
          maxLength={120}
          onChange={(e) => set("email", e.target.value)}
          required={isNew}
        />
      </div>
      {isNew && (
        <div className="form-field form-field-wide">
          <label>
            Password <span className="req">*</span> (≥ 6 chars)
          </label>
          <input
            data-testid="um-pwd"
            type="password"
            value={form.password}
            minLength={6}
            onChange={(e) => set("password", e.target.value)}
            required
          />
        </div>
      )}
    </>
  );
}

/* -- PKC member # + phone -- */
function ContactFieldsSection({ form, set }) {
  return (
    <>
      <div className="form-field">
        <label>PKC member #</label>
        <input
          data-testid="um-pkc-mem"
          type="number"
          inputMode="numeric"
          min={0}
          max={32767}
          value={form.pkc_mem ?? ""}
          onChange={(e) => set("pkc_mem", e.target.value)}
          placeholder="—"
        />
      </div>
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Phone <span className="muted" style={{ fontSize: 11 }}>(max 12 chars)</span></label>
        <input
          data-testid="um-phon-no"
          type="tel"
          maxLength={12}
          value={form.phon_no ?? ""}
          onChange={(e) => set("phon_no", e.target.value)}
          placeholder="e.g. 5551234567"
        />
      </div>
    </>
  );
}

/* -- Row 3+4: role / email / login method / password -- */
function UserPermissionsSection({ form, set, isSelf, isNew }) {
  return (
    <>
      <RoleEmailSection form={form} set={set} isSelf={isSelf} isNew={isNew} />
      <ContactFieldsSection form={form} set={set} />
    </>
  );
}

/* -- footer with delete + submit -- */
function UserModalActions({ isNew, isSelf, busy, canWrite = true, onDelete, onOpenPassword, submitLabel, deleteLabel = "Delete user", deleteArmed = false }) {
  let deleteTitle = "Delete this user";
  if (isSelf) deleteTitle = "Can't delete yourself";
  else if (deleteArmed) deleteTitle = "Click again to confirm";
  return (
    <div className="modal-actions no-divider" style={{ justifyContent: "space-between" }}>
      <div style={{ display: "flex", gap: 10 }}>
        {!isNew && canWrite && (
          <button
            type="button"
            className="btn-ghost"
            data-testid="um-password-btn"
            onClick={onOpenPassword}
            disabled={busy}
          >Password</button>
        )}
        {!isNew && canWrite && (
          <button
            type="button"
            className="btn-danger"
            data-testid="um-delete"
            onClick={onDelete}
            disabled={busy || isSelf}
            title={deleteTitle}
          >{deleteLabel}</button>
        )}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        {canWrite && (
          <button
            type="submit"
            className="btn-primary"
            data-testid="um-submit"
            disabled={busy}
            style={{ width: "auto", padding: "10px 22px", margin: 0 }}
          >
            {submitLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* -- diff helper: build PATCH payload from the form -- */
// pkc_mem: form holds a string; treat "" as unchanged when the row already
// has no number, else send the parsed int. Clearing is intentionally not
// supported here to avoid clobbering existing values with an accidentally
// emptied input.
function applyPkcMemPatch(patch, form, user) {
  const curPkc = user.pkc_mem == null ? "" : String(user.pkc_mem);
  const formPkc = form.pkc_mem ?? "";
  if (String(formPkc) === curPkc) return;
  if (formPkc !== "" && formPkc != null) patch.pkc_mem = Number(formPkc);
}

function buildUserPatch(form, user) {
  const patch = {};
  if (form.first_name !== user.first_name) patch.first_name = form.first_name;
  if (form.last_name !== user.last_name) patch.last_name = form.last_name;
  if (form.email !== user.email) patch.email = form.email;
  if (form.user_id !== user.user_id) patch.user_id = form.user_id;
  if (form.user_ty !== user.user_ty) patch.user_ty = form.user_ty;
  if (form.client !== (user.client || "")) patch.client = form.client || null;
  applyPkcMemPatch(patch, form, user);
  const curPhone = user.phon_no || "";
  if ((form.phon_no || "") !== curPhone) patch.phon_no = form.phon_no || "";
  return patch;
}

/* -- validation for the Set Password modal -- */
function validatePasswordPair(pwd1, pwd2) {
  if (pwd1 !== pwd2) return "Passwords do not match";
  if (pwd1.length < 6) return "Password must be at least 6 characters";
  return null;
}

/* -- success view once the password has been saved -- */
function SetPasswordSuccess({ onClose }) {
  return (
    <div className="modal-form">
      <div className="form-success" data-testid="spm-success">Password updated.</div>
      <div className="modal-actions no-divider" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn-primary" style={{ width: "auto", padding: "10px 22px", margin: 0 }} onClick={onClose} data-testid="spm-done">Done</button>
      </div>
    </div>
  );
}

/* -- the two-field password form -- */
function SetPasswordForm({ pwd1, pwd2, err, busy, onChange1, onChange2, onSubmit }) {
  return (
    <form className="modal-form" onSubmit={onSubmit} noValidate>
      <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
        <div className="form-field">
          <label>New password <span className="req">*</span> (≥ 6 chars)</label>
          <input
            data-testid="spm-pwd1"
            type="password"
            value={pwd1}
            autoFocus
            onChange={onChange1}
            required
          />
        </div>
        <div className="form-field">
          <label>Confirm password <span className="req">*</span></label>
          <input
            data-testid="spm-pwd2"
            type="password"
            value={pwd2}
            onChange={onChange2}
            required
          />
        </div>
      </div>
      {err && <div className="form-error" data-testid="spm-error">{err}</div>}
      <div className="modal-actions no-divider" style={{ justifyContent: "flex-end" }}>
        <button
          type="submit"
          className="btn-primary"
          data-testid="spm-save"
          disabled={busy}
          style={{ width: "auto", padding: "10px 22px", margin: 0 }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

/* -- standalone "set password" modal, opened via the Password button -- */
function SetPasswordModal({ userId, onClose }) {
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    const validationErr = validatePasswordPair(pwd1, pwd2);
    if (validationErr) { setErr(validationErr); return; }
    setBusy(true);
    try {
      await api.updateUser(userId, { password: pwd1 });
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 420 }}
        data-testid="set-password-modal"
      >
        <div className="modal-head">
          <h2>Set password · {userId}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="spm-close">×</button>
        </div>
        {done ? (
          <SetPasswordSuccess onClose={onClose} />
        ) : (
          <SetPasswordForm
            pwd1={pwd1}
            pwd2={pwd2}
            err={err}
            busy={busy}
            onChange1={(e) => { setPwd1(e.target.value); setErr(""); }}
            onChange2={(e) => { setPwd2(e.target.value); setErr(""); }}
            onSubmit={save}
          />
        )}
      </div>
    </div>
  );
}

function UserModal({ mode, user, currentUserId, currentUserClient, canWrite = true, onClose, onSaved, onManageAuth }) {
  const isNew = mode === "new";
  // SUPER admins can place new users into any tenant (matches the backend
  // tenancy guard in /api/users, which exempts client.upper() === 'SUPER').
  const isSuperAdmin = (currentUserClient || "").toUpperCase() === "SUPER";
  const [form, setForm] = useState(
    isNew ? { ...emptyNew, client: (!isSuperAdmin && currentUserClient) ? currentUserClient : "" } : {
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      user_id: user.user_id || "",
      user_ty: user.user_ty ?? "M",
      client: user.client || "",
      password: "",
      pkc_mem: user.pkc_mem == null ? "" : String(user.pkc_mem),
      phon_no: user.phon_no || "",
    }
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [clients, setClients] = useState([]);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const isSelf = !isNew && user.user_id === currentUserId;
  // Lock the Client dropdown when editing (task 2) or when the signed-in
  // admin already has a client and is creating a new user (task 3) — but
  // never lock SUPER admins, who must be able to pick any tenant.
  const lockClient = !isNew || (isNew && !!currentUserClient && !isSuperAdmin);

  // Load the client roster once for the dropdown
  useEffect(() => {
    api.listClients().then((rows) => setClients(rows || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const liveInit = initialsOf(form.first_name, form.last_name);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (isNew) {
        await api.createUser({
          ...form,
          client: form.client || null,
          pkc_mem: form.pkc_mem === "" || form.pkc_mem == null
            ? null : Number(form.pkc_mem),
          phon_no: form.phon_no || null,
        });
      } else {
        const patch = buildUserPatch(form, user);
        if (Object.keys(patch).length) {
          await api.updateUser(user.user_id, patch);
        }
        onManageAuth();
        return;
      }
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (isSelf) return;
    // Two-click confirmation: first click arms the button, second click deletes.
    if (!confirmDel) {
      setErr("");
      setConfirmDel(true);
      return;
    }
    setErr("");
    setBusy(true);
    try { await api.deleteUser(user.user_id); onSaved(); }
    catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setConfirmDel(false);
    }
    finally { setBusy(false); }
  };

  let submitLabel = "User Auth";
  if (busy) submitLabel = "Saving…";
  else if (isNew) submitLabel = "Create user";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 1280 }}
        data-testid="user-modal"
      >
        <div className="modal-head">
          <h2>
            {isNew ? "New User" : `Edit user · ${user.user_id}`}
            {isSelf && <span className="muted" style={{ fontSize: 13, marginLeft: 6 }}>(you)</span>}
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="um-close">×</button>
        </div>
        <form className="modal-form" onSubmit={submit}>
          <div className="form-grid">
            <UserIdentitySection form={form} set={set} isSelf={isSelf} isNew={isNew} liveInit={liveInit} clients={clients} lockClient={lockClient} />
            <UserNameSection form={form} set={set} isNew={isNew} />
            <UserPermissionsSection form={form} set={set} isSelf={isSelf} isNew={isNew} />
          </div>
          {err && <div className="form-error" data-testid="um-error">{err}</div>}
          <UserModalActions
            isNew={isNew}
            isSelf={isSelf}
            busy={busy}
            canWrite={canWrite}
            onDelete={onDelete}
            onOpenPassword={() => setShowPwdModal(true)}
            submitLabel={submitLabel}
            deleteLabel={confirmDel ? "Click again to confirm" : "Delete user"}
            deleteArmed={confirmDel}
          />
        </form>
      </div>
      {showPwdModal && (
        <SetPasswordModal userId={user.user_id} onClose={() => setShowPwdModal(false)} />
      )}
    </div>
  );
}

/* -- single row in the users table -- */
function UserRow({ u, currentUserId, onClick }) {
  const isMe = u.user_id === currentUserId;
  return (
    <tr
      onClick={onClick}
      data-testid={`user-row-${u.user_id}`}
      style={{ cursor: "pointer" }}
    >
      <td>
        <b>{u.user_id}</b>
        {isMe && (
          <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>(you)</span>
        )}
      </td>
      <td className="muted">{u.user_init || initialsOf(u.first_name, u.last_name)}</td>
      <td>{u.first_name} {u.last_name}</td>
      <td className="muted">{u.email}</td>
      <td className="muted">{u.client || "—"}</td>
      <td>{TYPE_LABEL[u.user_ty] || `Type ${u.user_ty}`}</td>
      <td className="muted" data-testid={`user-pkc-${u.user_id}`}>{u.pkc_mem ?? "—"}</td>
      <td className="muted" data-testid={`user-phone-${u.user_id}`}>{u.phon_no || "—"}</td>
    </tr>
  );
}

export default function AdminUsers({ currentUserId, currentUserClient = "", open = false, onToggle, standalone = false, canWrite = true }) {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);
  const [authTarget, setAuthTarget] = useState(null);
  const [clientFilter, setClientFilter] = useState("");
  const [page, setPage] = useState(0);
  const isOpen = standalone || open;
  const isSuperClient = (currentUserClient || "").toUpperCase() === "SUPER";

  const load = async () => {
    try { setUsers(await api.listUsers()); }
    catch (e) { setErr(e.message); }
  };
  useEffect(() => { if (isOpen) load(); }, [isOpen]);

  const clients = useMemo(() => {
    if (!users) return [];
    const seen = new Set();
    users.forEach((u) => seen.add(u.client || ""));
    return Array.from(seen).sort();
  }, [users]);

  const visibleUsers = useMemo(() => {
    if (!users) return null;
    if (!clientFilter) return users;
    return users.filter((u) => (u.client || "") === clientFilter);
  }, [users, clientFilter]);

  useEffect(() => { setPage(0); }, [clientFilter]);
  const pagedUsers = useMemo(
    () => (visibleUsers ? visibleUsers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) : null),
    [visibleUsers, page]
  );

  return (
    <>
      <section className={`admin-section ${isOpen ? "open" : "closed"} ${standalone ? "standalone" : ""}`} data-testid="admin-section-users">
        <div
          className="admin-section-header"
          role={standalone ? undefined : "button"}
          tabIndex={standalone ? undefined : 0}
          data-testid="admin-toggle-users"
          onClick={standalone ? undefined : onToggle}
          onKeyDown={standalone ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle && onToggle(); } }}
          aria-expanded={standalone ? undefined : isOpen}
        >
          {!standalone && <span className="admin-caret">▾</span>}
          <p className="section-title" style={{ margin: 0 }}>Users</p>
        </div>
        <div className="admin-section-body">
          <div className="card list-card" data-testid="admin-users-card">
            {err && (
              <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>
            )}
            {!users ? (
              <div className="loading-row"><span className="spinner" /> Loading users…</div>
            ) : (
              <>
                <div className="list-header">
                  <div className="list-header-left">
                    <label className="budget-label" htmlFor="user-client-filter">Client</label>
                    <select
                      id="user-client-filter"
                      className="fy-select"
                      data-testid="user-client-filter"
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                      style={{ minWidth: 200 }}
                    >
                      <option value="">{isSuperClient ? "All clients" : (currentUserClient || "All clients")}</option>
                      {clients.map((c) => (
                        <option key={c || "_"} value={c}>{c || "(none)"}</option>
                      ))}
                    </select>
                    <span className="list-count count" data-testid="user-count">
                      {visibleUsers.length} of {users.length}
                    </span>
                  </div>
                  {canWrite && (
                    <div className="list-header-right">
                      <button
                        type="button"
                        className="btn-navy"
                        data-testid="new-user-btn"
                        onClick={() => setModal({ mode: "new" })}
                      >+ New user</button>
                    </div>
                  )}
                </div>
                <div className="list-scroll">
                <table className="list-table list-table-compact txn-table">
                  <thead>
                    <tr className="list-head-labels">
                      <th>User ID</th>
                      <th>Initials</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Client</th>
                      <th>Type</th>
                      <th>PKC #</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedUsers.map((u) => (
                      <UserRow
                        key={u.user_id}
                        u={u}
                        currentUserId={currentUserId}
                        onClick={() => setModal({ mode: "edit", user: u })}
                      />
                    ))}
                  </tbody>
                </table>
                </div>

                {visibleUsers.length > PAGE_SIZE && (
                  <div className="list-pager" data-testid="users-pager">
                    <span className="list-pager-info" data-testid="users-pager-info">
                      Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, visibleUsers.length)} of {visibleUsers.length}
                    </span>
                    <div className="list-pager-btns">
                      <button className="btn-ghost" data-testid="users-prev"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
                      <button className="btn-ghost" data-testid="users-next"
                        disabled={(page + 1) * PAGE_SIZE >= visibleUsers.length}
                        onClick={() => setPage((p) => p + 1)}>Next ›</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          currentUserId={currentUserId}
          currentUserClient={currentUserClient}
          canWrite={canWrite}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          onManageAuth={() => {
            const target = modal.user;
            setModal(null);
            load();
            setAuthTarget(target);
          }}
        />
      )}

      {authTarget && (
        <UserAuthModal
          user={authTarget}
          canWrite={canWrite}
          currentUserId={currentUserId}
          isSuperClient={isSuperClient}
          onClose={() => setAuthTarget(null)}
        />
      )}
    </>
  );
}
